import _ from 'underscore';
import $ from 'jquery';
import Backbone from 'backbone';
import ko from 'knockout';
import { generateFieldOptions } from './field/generate';
import { generateSidebarsOptions } from './sidebar/generator';
import { isBackboneView, bindTo } from './util';
import tmplSidebarWrapper from '../template/sidebar/wrapper.pug';
import tmplHorizontal from '../template/simple-form/layout/horizontal.pug';
import tmplInline from '../template/simple-form/layout/inline.pug';
import tmplLabel from '../template/simple-form/control/label.pug';
import tmplButtons from '../template/simple-form/control/buttons.pug';
import tmplValidation from '../template/simple-form/control/validation.pug';
import { SimpleFormViewModel } from '../viewmodel/simple-form';

/**
 * define Object Field
 * @typedef {String|Object} Field
 * it can be a string of property name
 * it also can be a object with property, control and other fileds options
 */

/**
 * define Object S$I18n
 * @typedef {Object} S$I18n
 * @property {function} type - get i18n string by schema
 * @property {function} property - get i18n string by schema and property name
 * @property {function} value - get i18n string by schema, property and value
 */

/**
 * define Object FieldOption
 * @typedef {Object} FieldOption
 * @property {boolean} showLabel - whether show lable
 * @property {Object} schema - property define in schema
 * @property {String} message - message under property input
 * @property {String} id - id of filed
 * @property {String|Object} label - property label
 * @property {boolean} visible - whether field visible
 * @property {boolean} required - whether property is required
 * @property {boolean} hasSubFields - whether field has subfields
 * @property {FieldOption[]} fields - subfields of field
 */


export class SimpleForm extends Backbone.View {
  /**
   * init simple form view, it will generate a view base on schema
   * and field, it will enter in edit mode if existing id in model,
   * you can customize validation in CustomValidators, when you have
   * some sidebar view for specific property, you can add it in sidebars,
   * about layout between properties, we provide two default template "horizontal"
   * and "inline", if you want to customzie your template you can pass a function
   * to template, at the same time, you can config whether have labels, buttons and buttons'
   * text as you need, if you have help icon in simple form for labels, please provide
   * a callback function clickHelpIcon to process click event of icon
   * @public
   * @event "save" event is triggered when click save button and pass current model as arg
   * @event "cancel" event is triggered when click cancel button and pass current model as arg
   * @event "clickHelpIcon" event is triggered when click .helpbutton in simple form
   * @param {JsonSchema} schema - entity define
   * @param {Field[]} fields - list of field
   * @param {Object} [model] - undefined
   * @param {CustomValidators} [customValidators] - {}
   * @param {SideBarOption[]} [sidebars] - []
   * @param {String|function} [template] - horizontal
   * @param {function} [labelTemplate] - src/template/simple-form/control/label.pug
   * @param {boolean} [showButtons] - true
   * @param {boolean} [hasLabels] - true
   * @param {String} [customLabel] - custom label
   * @param {String} [customHint] - custom hint
   * @param {String} [inputSuffix] - input suffix
   * @param {S$I18n} [s$i18n] - get i18n string of schema
   * @param {function} [s$i18n.type=identity] - get string with schema name
   * @param {function} [s$i18n.property=property] - get string of a property in schema
   * @param {function} [s$i18n.value=value] - get string of a value enumed in property of schema
   * @param {I18n} [i18n] - get i18n string in component
   * @param {function} [i18n.getString=identity] - get string with key
   * @param {function} [i18n.isValidDecimal] - whether a string is valid decimal
   * @param {function} [i18n.parseDecimal=parseFloat] - parse string to float
   * @param {function} [i18n.formatDecimal] - convert float to string
   * @returns {void}
   */
  initialize({
    schema,
    fields,
    model,
    customValidators = {},
    sidebars = [],
    template = 'horizontal',
    labelTemplate = tmplLabel,
    showButtons = true,
    hasLabels = true,
    customLabel = null,
    customHint = null,
    inputSuffix = null,
    s$i18n = {
      type(type) {
        return type;
      },
      property(type, property) {
        return property;
      },
      value(type, property, value) {
        return String(value);
      },
    },
    i18n = {
      getString: _.identity,
      isValidDecimal: value => !_.isNaN(parseFloat(value)),
      parseDecimal: str => parseFloat(str),
      formatDecimal: float => `${float}`,
      formatDecimalToFixed: float => `${float}`,
    },
  }) {
    this.i18n = i18n;

    if (_.isFunction(template)) {
      this.template = template;
    } else {
      this.template = {
        horizontal: tmplHorizontal,
        inline: tmplInline,
      }[template];
    }

    this.model = model;
    this.type = schema.name;
    this.id = `${_.uniqueId('Form-')}-${schema.name}`;

    this.fields = generateFieldOptions(schema, fields, this.id, s$i18n);

    this.flattedFields = this.getFlattedFields(this.fields);

    this.properties = _.chain(this.flattedFields)
      .filter(field => field.hasSubFields !== true)
      .pluck('property')
      .value();

    this.sidebars = generateSidebarsOptions({ schema, properties: this.properties, sidebars });

    this.fieldIndex = _.indexBy(this.flattedFields, field => field.property);

    this.viewModel = new SimpleFormViewModel({
      fields: this.flattedFields,
      schema,
      model: this.model,
      customValidators,
      i18n,
    });

    _.each(this.properties, (prop) => {
      this.viewModel[prop].subscribe((val) => {
        this.trigger(`change:${prop}`, val);
        this.trigger('change', this.currentModel());
      });
    });

    this.labelTemplate = labelTemplate;
    this.showButtons = showButtons;
    this.saveButtonText = i18n.getString('SimpleForm_Button_Save');
    this.cancelButtonText = i18n.getString('SimpleForm_Button_Cancel');
    this.hasLabels = hasLabels;
    this.customLabel = customLabel;
    this.customHint = customHint;
    this.inputSuffix = inputSuffix;
  }

  get events() {
    return {
      'click .save': 'save',
      'click .cancel': 'cancel',
      'submit form': 'save',
      'click .helpbutton': 'clickHelpIcon',
    };
  }

  /**
   * click help icon event handler
   * @private
   * @param {Event} e - click event on help icon
   * {@link https://developer.mozilla.org/en/docs/Web/API/Event Event}
   * @returns {void}
   */
  clickHelpIcon(e) {
    this.trigger('clickHelpIcon', e);
  }

  /**
   * render view
   * @public
   * @returns {View} - return view itself
   */
  render() {
    this.$el.html(this.template({
      i18n: this.i18n,
      properties: this.properties,
      fields: this.fields,
      indexedFields: this.fieldIndex,
      showButtons: this.showButtons,
      hasLabels: this.hasLabels,
      customHint: this.customHint,
      inputSuffix: this.inputSuffix,
      type: this.type,
      bindTo,
    }));

    this.renderBuiltInControls();
    this.renderTemplateControls();
    this.renderLabels();
    this.renderValidations();

    ko.applyBindings(this.viewModel, this.el);

    this.renderViewControls();
    this.renderSidebars();
    return this;
  }

  /**
   * remove this view and it's subview
   * @public
   * @returns {void}
   */
  remove() {
    _.each(this.viewControls, (viewControl) => {
      viewControl.remove();
    });

    _.each(this.sidebarViews, (sidebarView) => {
      sidebarView.remove();
    });

    super.remove();
  }

  /**
   * get flatted fields, we can define field in another field
   * @param {FieldOption[]} fields - field
   * @returns {FieldOption[]} - flatted field
   * @private
   */
  getFlattedFields(fields) {
    const ret = _.map(fields, (field) => {
      let subFields = [];

      if (field.hasSubFields === true) {
        subFields = this.getFlattedFields(field.fields);
      }

      subFields.push(field);
      return subFields;
    });

    return _.flatten(ret);
  }

  /**
   * render buildin controls for all properties input
   * @returns {void}
   * @private
   */
  renderBuiltInControls() {
    this.$('.form-input-container[data-property="$buttons"]').html(tmplButtons(_.pick(this, ['saveButtonText', 'cancelButtonText'])));
  }

  /**
   * render customized template controal
   * @returns {void}
   * @private
   */
  renderTemplateControls() {
    this.$('.form-input-container').each((i, e) => {
      const $el = $(e);
      const property = $el.attr('data-property');
      const field = this.fieldIndex[property] || {};
      const { control } = field;

      if (!isBackboneView(control) && control) {
        if (this.inputSuffix) {
          control.html += `<span class="input-suffix">${this.inputSuffix}</span>`;
        }
        $el.html(control.html);
      }
    });
  }

  /**
   * render customized view control
   * @returns {void}
   * @private
   */
  renderViewControls() {
    this.viewControls = {};

    this.$('.form-input-container').each((i, e) => {
      const $el = $(e);
      const property = $el.attr('data-property');
      const field = this.fieldIndex[property] || {};
      const Constructor = field.control;

      if (isBackboneView(Constructor)) {
        this.viewControls[property] = new Constructor({
          observable: this.viewModel[property],
          viewModel: this.viewModel,
          field,
        });

        $el.append(this.viewControls[property].render().el);
      }
    });
  }

  /**
   * render labels for all properties, used to show labels of property and help icon
   * @returns {void}
   * @private
   */
  renderLabels() {
    this.$('.form-label-container').each((i, e) => {
      const $el = $(e);
      const field = this.fieldIndex[$el.attr('data-property')];

      if (field.showLabel) {
        const learnMoreText = field.schema.helpId ? this.i18n.getString('SimpleForm_LearnMore', {
          item: field.label,
        }) : '';
        $el.html(this.labelTemplate({
          field,
          type: this.type,
          bindTo,
          learnMoreText,
          customLabel: this.customLabel,
        }));
      }
    });
  }

  /**
   * render validation template for all properties, used to show validate results
   * @returns {void}
   * @private
   */
  renderValidations() {
    this.$('.form-validation-container').each((i, e) => {
      const $el = $(e);
      const property = $el.attr('data-property');
      const field = this.fieldIndex[property] || { property, id: `${this.id}-${property}` };
      const observable = this.viewModel[property];

      if (_.isFunction(observable.validate)) {
        $el.html(tmplValidation({
          field,
          bindTo,
        }));
      }
    });
  }

  /**
   * render sidebars
   * @returns {void}
   * @private
   */
  renderSidebars() {
    this.sidebarViews = {};

    _.each(this.sidebars, ({ properties, view: Ctor = {} }, strProperties) => {
      const selector = _.map(properties, property => `.form-group[data-property=${property}]`).join(',');

      this.$(selector).wrapAll(tmplSidebarWrapper({ properties }));

      if (isBackboneView(Ctor)) {
        const sidebar = new Ctor({
          className: 'sidebar',
          id: `${this.id}-${strProperties}`,
          properties,
          viewModel: this.viewModel,
          i18n: this.i18n,
        });

        this.$(`.form-groups-with-sidebar[data-properties='${strProperties}']`).append(sidebar.render().el);

        this.sidebarViews[strProperties] = sidebar;
      }
    });
  }

  /**
   * get current model of simple form
   * @public
   * @returns {Object} - return current value model of simple form
   */
  currentModel() {
    return _.defaults(this.viewModel.toObject(), this.model);
  }

  /**
   * convert and set error message to each property
   * @public
   * @param {Object[]} errors - each item has property and message
   * @param {Object} model - value model of simple form
   * @returns {void}
   */
  setErrors(errors, model) {
    const modelErr = model || this.currentModel();

    _.each(errors, (message, prop) => {
      const observable = this.viewModel[prop];

      if (_.isArray(message)) {
        const proxy = observable || this.viewModel.$overall;
        const uniqMessage = _.uniq(message, false, msg => msg.message);

        proxy.validate();
        proxy.errors.push(...uniqMessage);
      } else if (observable && observable.validators) {
        const validator = observable.validators.passive;

        if (validator && _.isFunction(validator.setError)) {
          validator.setError(message, modelErr[prop]);
        }

        observable.validate();
      }
    });
  }

  /**
   * validate simple form forcely
   * @public
   * @returns {void}
   */
  validate() {
    return this.viewModel.validate();
  }

  /**
   * validate and trigger 'save' event when click save button in simple form
   * @private
   * @param {Event} e - event passed from backbone view
   * @returns {void}
   */
  save(e) {
    if (e && e.type === 'submit') {
      // Form submission will cause page redirection; stop it here
      e.preventDefault();
    }

    if (this.validate()) {
      this.trigger('save', this.currentModel());
    }
  }

  /**
   * trigger 'cancel' event when click cancel button in simple form
   * @private
   * @returns {void}
   */
  cancel() {
    this.trigger('cancel', this.currentModel());
  }
}
